// 家用面板 P4 卡片（docs/panel 05-ui-ux）：V-02 待辦與 PIN 狀態、V-06 門鈴與警報 / 開門、V-10 家人、V-15 操作紀錄、
// V-22 快速設定；V-11 我的面板 PIN 與「可以幫面板確認的手機」放在帳號頁（MyPanelPinCard、MyConfirmKeysCard）。只有主人看得到這些（分享者沒有面板設定權）。
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { deviceName, isPanelDevice, type DeviceSummary } from './deviceModel';
import { CameraPinAsk, CameraPinToggle } from './PanelCameraPin';
import {
  accountFamily, activeUntil, alarmIdsOf, applyQuickSetup, askPanelHome, createPanelMember, formatHm, getPanelHome, panelHomeCardState, panelTodoAction, setPanelHomeMode, createSetupCode, deleteMyPanelPin, deletePanelMember,
  dismissPanelTodo, doorbellIdsOf, formatSetupCode, getMyPanelPin, getPanelActivity, getPanelCaps, getPanelSettings, getPanelTodo,
  listPanelMembers, listPanelPinBlocks, panelActivityKey, panelActivityLevelKey, parseBellSettings, pinFormatError, putMyPanelPin, putPanelSettings,
  cameraUntagged, quickSetupDefaults, resetPanelMemberPin, resumePanelPin, setCameraLocation, setPanelPinBlock, todoI18nKey,
  confirmKeyCooling, confirmKeyHistoryKey, deleteConfirmKey, listConfirmKeys, sameMethods, toggleUnlockMethod,
  type ConfirmKeyList, type MyPanelPin, type PanelActivityRow, type PanelHomeInfo, type PanelHomeMode, type PanelBellSettings, type PanelMember, type PanelSetupCode, type PanelTodo,
  type QuickSetup, type ShareLikeRow,
} from './panels';

const fmtS = (s: number) => formatTimestampUs(s * 1_000_000);
/** 參數是空的時候收掉「（）」「()」 */
const stripEmptyParens = (s: string) => s.replace(/\s*[（(]\s*[)）]/g, '');

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'PIN_WEAK') return t('panels.pin.weak');
    if (e.code === 'NOT_HOUSEHOLD') return t('panels.mem.notHousehold');
    if (e.code === 'PANEL_MANAGED') return t('panels.share.managed');
  }
  return t('panels.errGeneric');
}

/** V-02 待辦清單 + 需要主人處理的 PIN 狀態 */
export function PanelTodoCard({ panelId, reloadKey = 0, onHomeChanged }: { panelId: string; reloadKey?: number; onHomeChanged?: () => void }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<PanelTodo[] | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [pin, setPin] = useState<{ paused_until?: number; disabled?: boolean }>({});
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    getPanelTodo(panelId).then(setItems).catch(() => setMsg(t('panels.errGeneric')));
    getPanelSettings(panelId).then((s) => setDismissed(parseBellSettings(s).todo_dismissed)).catch(() => undefined);
    // 雲端若帶出整台 PIN 暫停 / 停用（pin_paused_until / pin_disabled）就顯示［立即恢復］/［解除］
    getPanelCaps(panelId)
      .then((c) => {
        const r = c as unknown as Record<string, unknown>;
        setPin({ paused_until: Number(r.pin_paused_until) || undefined, disabled: r.pin_disabled === true });
      })
      .catch(() => undefined);
  }, [panelId, t]);
  useEffect(load, [load, reloadKey]);

  const dismiss = async (key: string) => {
    try {
      await dismissPanelTodo(panelId, key, dismissed);
      load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const askHome = async () => {
    try {
      await askPanelHome(panelId);
      setMsg(t('panels.todo.askHomeSent'));
      load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  // 一般使用 / 暫停中 → 回到面板模式（一般使用改回時平板會跳一次「設為主畫面」系統確認）
  const homePanel = async (key: string) => {
    try {
      await setPanelHomeMode(panelId, 'panel');
      setMsg(t(key === 'home_normal' ? 'panels.home.panelSent' : 'panels.home.sent'));
      load();
      onHomeChanged?.();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const resume = async () => {
    try {
      await resumePanelPin(panelId);
      setMsg(t('panels.todo.resumed'));
      load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const paused = pin.paused_until && pin.paused_until > Date.now() / 1000;

  return (
    <div className="card" data-testid="panel-todo">
      <h2>{t('panels.todo.title')}</h2>
      {(paused || pin.disabled) && (
        <p className="error-text" data-testid="panel-pin-paused">
          {pin.disabled ? t('panels.todo.pinDisabled') : t('panels.todo.pinPaused', { time: fmtS(pin.paused_until!) })}{' '}
          <button className="btn-ghost" onClick={() => void resume()}>
            {pin.disabled ? t('panels.todo.unblock') : t('panels.todo.resumeNow')}
          </button>
        </p>
      )}
      {items && items.length === 0 && <p className="text-secondary">{t('panels.todo.none')}</p>}
      <ul className="dv-panel-todo">
        {(items ?? []).map((it) => (
          <li key={it.key} data-testid={`todo-${it.key}`}>
            <span>{t(todoI18nKey(it), { count: it.count ?? 0, names: (it.names ?? []).join(t('sharing.permJoin')), time: it.until ? formatHm(it.until) : '' })}</span>
            {it.key === 'home_app' && (
              <button className="btn-ghost" data-testid="todo-home_app-ask" onClick={() => void askHome()}>
                {t('panels.todo.askHome')}
              </button>
            )}
            {panelTodoAction(it.key) === 'home_panel' && (
              <button className="btn-ghost" data-testid={`todo-${it.key}-panel`} onClick={() => void homePanel(it.key)}>
                {t(it.key === 'home_normal' ? 'panels.todo.homePanel' : 'panels.todo.homePanelNow')}
              </button>
            )}
            {it.dismissible && (
              <button className="btn-ghost" onClick={() => void dismiss(it.key)}>
                {t('panels.todo.dismiss')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {msg && <p className="text-secondary">{msg}</p>}
    </div>
  );
}

/**
 * 平板主畫面模式（Android 面板才有；iPad 的 home = null 不顯示）：面板模式 ⇄ 一般使用、暫停 10 分鐘。
 * 已送出、面板還沒處理（pending_mode）→ 按鈕停用，每 5 秒重讀一次直到面板回應。
 */
export function PanelHomeCard({ panelId, reloadKey = 0, onChanged }: { panelId: string; reloadKey?: number; onChanged?: () => void }) {
  const { t } = useTranslation();
  const [home, setHome] = useState<PanelHomeInfo | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmNormal, setConfirmNormal] = useState(false);

  const load = useCallback(() => {
    getPanelHome(panelId).then(setHome).catch(() => undefined);
  }, [panelId]);
  useEffect(load, [load, reloadKey]);
  const state = home ? panelHomeCardState(home, Date.now() / 1000) : null;
  useEffect(() => {
    if (state !== 'pending') return undefined;
    const h = setInterval(load, 5000);
    return () => clearInterval(h);
  }, [state, load]);

  if (!home || !state) return null;
  const send = async (mode: PanelHomeMode, okKey: string) => {
    setBusy(true);
    setMsg(null);
    setConfirmNormal(false);
    try {
      await setPanelHomeMode(panelId, mode);
      setMsg(t(okKey));
      load();
      onChanged?.();
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const off = busy || state === 'pending';

  return (
    <div className="card" data-testid="panel-home-card">
      <h2>{t('panels.home.title')}</h2>
      {state === 'pending' && <p data-testid="panel-home-pending">{t('panels.home.pending')}</p>}
      {state === 'paused' && (
        <p>
          {t('panels.todo.home_paused', { time: formatHm(home.paused_until) })}{' '}
          <button className="btn-ghost" data-testid="panel-home-panel" disabled={off} onClick={() => void send('panel', 'panels.home.sent')}>
            {t('panels.todo.homePanelNow')}
          </button>
        </p>
      )}
      {state === 'normal' && (
        <p>
          {t('panels.todo.home_normal')}{' '}
          <button className="btn-ghost" data-testid="panel-home-panel" disabled={off} onClick={() => void send('panel', 'panels.home.panelSent')}>
            {t('panels.todo.homePanel')}
          </button>
        </p>
      )}
      {state === 'panel' && (
        <>
          <p>{t('panels.home.modePanel')}</p>
          {!confirmNormal ? (
            <div className="dv-pairing-actions">
              <button className="btn-ghost" data-testid="panel-home-pause" disabled={off} onClick={() => void send('paused', 'panels.home.sent')}>
                {t('panels.home.pause')}
              </button>
              <button className="btn-ghost" data-testid="panel-home-normal" disabled={off} onClick={() => setConfirmNormal(true)}>
                {t('panels.home.normal')}
              </button>
            </div>
          ) : (
            <>
              <p>{t('panels.home.normalConfirm')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" data-testid="panel-home-normal-confirm" disabled={off} onClick={() => void send('normal', 'panels.home.sent')}>
                  {t('panels.home.normal')}
                </button>
                <button className="btn-ghost" onClick={() => setConfirmNormal(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
              </div>
            </>
          )}
        </>
      )}
      <p className="text-secondary">{t('panels.home.exitHint')}</p>
      {msg && <p className="text-secondary">{msg}</p>}
    </div>
  );
}

/**
 * V-06 門鈴與警報、開門設定。
 * D2（10 §4.1、M1、M4）：驗證方式 = 面板 P-08 會出現的分頁（`auth.tab.pin` / `auth.tab.phone`，至少留一個；沒改過就不送，
 * 雲端維持「沒存值 = 預設」）；手機確認可以開門（`phone_allows_door`）另外一列：說明句（`confirm.phone_door_setting`）+［打開］，
 * 按了立刻存（不跟著［儲存］，因為它是一個明確的同意動作）。網頁不用先解鎖手機（V-09 P1b 補：網頁不需要解鎖）。
 */
export function PanelBellCard({ panelId, devices }: { panelId: string; devices: DeviceSummary[] }) {
  const { t } = useTranslation();
  const [s, setS] = useState<PanelBellSettings | null>(null);
  /** 讀進來時的驗證方式：一樣就不送 unlock_methods */
  const [savedMethods, setSavedMethods] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doorBusy, setDoorBusy] = useState(false);
  const bells = useMemo(() => doorbellIdsOf(devices), [devices]);
  const alarms = useMemo(() => alarmIdsOf(devices), [devices]);
  const nameOf = useMemo(() => new Map(devices.map((d) => [d.device_id, deviceName(d)])), [devices]);

  useEffect(() => {
    getPanelSettings(panelId)
      .then((r) => {
        const p = parseBellSettings(r);
        setS(p);
        setSavedMethods(p.unlock_methods);
      })
      .catch(() => setMsg(t('panels.errGeneric')));
  }, [panelId, t]);

  if (!s) return <div className="card"><h2>{t('panels.bell.title')}</h2>{msg && <p className="error-text">{msg}</p>}</div>;
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const methodsChanged = !sameMethods(s.unlock_methods, savedMethods);
      await putPanelSettings(panelId, {
        doorbells: s.doorbells, ring: s.ring, alarms: s.alarms, allow_door_open: s.allow_door_open,
        pin_shuffle: s.pin_shuffle, control_window_s: s.control_window_s,
        ...(methodsChanged ? { unlock_methods: s.unlock_methods } : {}),
      });
      if (methodsChanged) setSavedMethods(s.unlock_methods);
      setMsg(t('panels.disp.saved'));
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };

  // 手機確認可以開門：按了就存（只送這一欄，表單其他沒存的改動不受影響）
  const setPhoneDoor = async (on: boolean) => {
    setDoorBusy(true);
    setMsg(null);
    try {
      await putPanelSettings(panelId, { phone_allows_door: on });
      setS((cur) => (cur ? { ...cur, phone_allows_door: on } : cur));
      setMsg(t(on ? 'confirm.phone_door_saved_on' : 'confirm.phone_door_saved_off'));
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setDoorBusy(false);
    }
  };

  return (
    <div className="card" data-testid="panel-bell">
      <h2>{t('panels.bell.title')}</h2>
      <h3>{t('panels.bell.doorbells')}</h3>
      {bells.length === 0 && <p className="text-secondary">{t('panels.qs.noDoorbell')}</p>}
      {bells.map((id) => (
        <label key={id} className="dv-check">
          <input type="checkbox" checked={s.doorbells.includes(id)} onChange={() => setS({ ...s, doorbells: toggle(s.doorbells, id) })} />
          {nameOf.get(id) ?? id}
        </label>
      ))}
      <label className="dv-check">
        <input type="checkbox" checked={s.ring} onChange={() => setS({ ...s, ring: !s.ring })} />
        {t('panels.bell.ring')}
      </label>
      <h3>{t('panels.bell.alarms')}</h3>
      <p className="text-secondary">{t('panels.bell.alarmsHint')}</p>
      {alarms.map((id) => (
        <label key={id} className="dv-check">
          <input type="checkbox" checked={s.alarms.includes(id)} onChange={() => setS({ ...s, alarms: toggle(s.alarms, id) })} />
          {nameOf.get(id) ?? id}
        </label>
      ))}
      <h3>{t('panels.bell.door')}</h3>
      <label className="dv-check">
        <input type="checkbox" checked={s.allow_door_open} onChange={() => setS({ ...s, allow_door_open: !s.allow_door_open })} data-testid="panel-allow-door" />
        {t('panels.bell.allowDoor')}
      </label>
      {s.allow_door_open && <p className="text-secondary">{t('panels.bell.allowDoorHint')}</p>}
      <h3>{t('confirm.methods_title')}</h3>
      <p className="text-secondary">{t('confirm.methods_hint')}</p>
      {(['pin', 'phone'] as const).map((m) => {
        const on = s.unlock_methods.includes(m);
        const last = on && s.unlock_methods.filter((x) => x === 'pin' || x === 'phone').length === 1;
        return (
          <label key={m} className="dv-check">
            <input type="checkbox" checked={on} disabled={last} onChange={() => setS({ ...s, unlock_methods: toggleUnlockMethod(s.unlock_methods, m) })}
              data-testid={`panel-method-${m}`} />
            {t(m === 'pin' ? 'auth.tab.pin' : 'auth.tab.phone')}
          </label>
        );
      })}
      {s.unlock_methods.includes('phone') && <p className="text-secondary">{t('confirm.method_phone_hint')}</p>}
      {s.allow_door_open && s.unlock_methods.includes('phone') && (
        <div className="dv-phone-door" data-testid="panel-phone-door">
          <p>{t('confirm.phone_door_setting')}</p>
          <p className="dv-pairing-actions">
            {s.phone_allows_door && <span data-testid="panel-phone-door-on">{t('confirm.phone_door_is_on')}</span>}
            <button className={s.phone_allows_door ? 'btn-ghost' : 'btn-primary'} disabled={doorBusy} onClick={() => void setPhoneDoor(!s.phone_allows_door)}
              data-testid="panel-phone-door-btn">
              {t(s.phone_allows_door ? 'confirm.btn.turn_off' : 'confirm.btn.turn_on')}
            </button>
          </p>
        </div>
      )}
      <label className="dv-check">
        <input type="checkbox" checked={s.pin_shuffle} onChange={() => setS({ ...s, pin_shuffle: !s.pin_shuffle })} />
        {t('panels.bell.shuffle')}
      </label>
      <label className="dv-pairing-input">
        <span>{t('panels.bell.window')}</span>
        <select value={s.control_window_s} onChange={(e) => setS({ ...s, control_window_s: Number(e.target.value) })}>
          {[30, 60, 120, 180, 300].map((v) => (
            <option key={v} value={v}>{t('panels.bell.windowSec', { s: v })}</option>
          ))}
        </select>
      </label>
      <div className="dv-pairing-actions">
        <button className="btn-primary" onClick={() => void save()} disabled={busy}>{t('panels.disp.save')}</button>
      </div>
      {msg && <p className="text-secondary">{msg}</p>}
    </div>
  );
}

/** V-10 家人：有帳號（在我的面板停用 PIN）+ 沒帳號（新增 / 設定碼 / 重設 PIN / 刪除）+ 鎖定狀態 */
export function PanelMembersCard({ panelId, devices }: { panelId: string; devices: DeviceSummary[] }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<PanelMember[] | null>(null);
  const [family, setFamily] = useState<{ user_id: string; name: string }[]>([]);
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [code, setCode] = useState<PanelSetupCode | null>(null);
  const [now, setNow] = useState(Date.now() / 1000);
  const [nick, setNick] = useState('');
  const [door, setDoor] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const panels = useMemo(() => devices.filter((d) => d.role === 'owner' && isPanelDevice(d)), [devices]);
  const [pick, setPick] = useState<string[]>([panelId]);

  const load = useCallback(async () => {
    try {
      setMembers(await listPanelMembers());
      setBlocked(new Set((await listPanelPinBlocks()).map((b) => b.user_id)));
      const owned = devices.filter((d) => d.role === 'owner' && !isPanelDevice(d));
      const rows = await Promise.all(
        owned.map((d) =>
          apiFetch<ShareLikeRow[]>(`/v3/devices/${encodeURIComponent(d.device_id)}/shares`, { query: { limit: 50 } })
            .then((r) => r.data ?? [])
            .catch(() => [] as ShareLikeRow[]),
        ),
      );
      setFamily(accountFamily(rows));
    } catch (e) {
      setMsg(errText(t, e));
    }
  }, [devices, t]);
  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (!code) return;
    const h = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(h);
  }, [code]);

  const run = async (f: () => Promise<unknown>, ok?: string) => {
    setMsg(null);
    try {
      await f();
      if (ok) setMsg(ok);
      await load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const add = () => {
    if (!nick.trim()) return;
    void run(async () => {
      await createPanelMember(nick.trim(), door ? ['view', 'door_open'] : ['view'], pick);
      setNick('');
    });
  };
  const genCode = (m: PanelMember) => run(async () => setCode(await createSetupCode(panelId, m.member_id)));
  const left = code ? Math.max(0, Math.round(code.expires_at - now)) : 0;

  return (
    <div className="card" data-testid="panel-members">
      <h2>{t('panels.mem.title')}</h2>
      <h3>{t('panels.mem.withAccount')}</h3>
      {family.length === 0 && <p className="text-secondary">{t('panels.mem.noFamily')}</p>}
      {/* pin_block 是主人層級，D2 起連手機確認一起停用（10 §2.5 第 3 點、M12）；他自己在 App 上的操作不受影響 */}
      {family.map((f) => (
        <div key={f.user_id} className="dv-member-row" data-testid={`family-${f.user_id}`}>
          <div>
            <span>{f.name}</span>
            {blocked.has(f.user_id) && <div className="error-text" data-testid={`family-${f.user_id}-blocked`}>{t('confirm.member_blocked')}</div>}
          </div>
          <button className="btn-ghost" onClick={() => void run(() => setPanelPinBlock(f.user_id, !blocked.has(f.user_id)))}>
            {blocked.has(f.user_id) ? t('panels.mem.unblock') : t('confirm.member_block')}
          </button>
        </div>
      ))}
      <p className="text-secondary">{t('confirm.member_block_hint')}</p>

      <h3>{t('panels.mem.noAccount')}</h3>
      {(members ?? []).map((m) => {
        const locks = activeUntil(m.locks, now);
        const paused = m.owner_pause && m.owner_pause > now ? m.owner_pause : 0;
        return (
          <div key={m.member_id} className="dv-member-row" data-testid={`member-${m.member_id}`}>
            <div>
              <strong>{m.nickname}</strong>{' '}
              <span className="text-secondary">
                {m.perms.includes('door_open') ? t('panels.mem.canDoor') : t('panels.mem.viewOnly')} · {m.pin_set ? t('panels.mem.pinSet') : t('panels.mem.pinNotSet')}
              </span>
              {locks.map(([pid, until]) => (
                <div key={pid} className="error-text">{t('panels.mem.lockedUntil', { time: fmtS(until), panel: deviceName(devices.find((d) => d.device_id === pid) ?? ({ device_id: pid } as DeviceSummary)) })}</div>
              ))}
              {paused > 0 && <div className="error-text">{t('panels.mem.pausedNoAccount', { time: fmtS(paused) })}</div>}
            </div>
            <div className="dv-pairing-actions">
              {m.panels.includes(panelId) && <button className="btn-ghost" onClick={() => void genCode(m)}>{t('panels.mem.genCode')}</button>}
              <button className="btn-ghost" onClick={() => void run(async () => { await resetPanelMemberPin(m.member_id); setCode(await createSetupCode(panelId, m.member_id)); })}>
                {t('panels.mem.resetPin')}
              </button>
              <button className="btn-danger" onClick={() => void run(() => deletePanelMember(m.member_id))}>{t('panels.mem.delete')}</button>
            </div>
          </div>
        );
      })}
      {code && left > 0 && (
        <div className="dv-setup-code" data-testid="panel-setup-code" aria-live="polite">
          <p className="dv-panel-match">{formatSetupCode(code.setup_code)}</p>
          <p className="text-secondary">{t('panels.mem.codeHint', { m: Math.floor(left / 60), s: String(left % 60).padStart(2, '0') })}</p>
        </div>
      )}
      <div className="dv-member-add">
        <input placeholder={t('panels.mem.nickname')} value={nick} maxLength={20} onChange={(e) => setNick(e.target.value)} data-testid="member-nick" />
        <label className="dv-check">
          <input type="checkbox" checked={door} onChange={() => setDoor(!door)} />
          {t('panels.mem.canDoor')}
        </label>
        {panels.map((p) => (
          <label key={p.device_id} className="dv-check">
            <input type="checkbox" checked={pick.includes(p.device_id)}
              onChange={() => setPick(pick.includes(p.device_id) ? pick.filter((x) => x !== p.device_id) : [...pick, p.device_id])} />
            {deviceName(p)}
          </label>
        ))}
        <button className="btn-primary" onClick={add} disabled={!nick.trim() || pick.length === 0}>{t('panels.mem.add')}</button>
      </div>
      {msg && <p className="text-secondary">{msg}</p>}
    </div>
  );
}

/** V-15 面板相關操作紀錄（開門 / 警報 / 面板） */
export function PanelActivityCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const [cat, setCat] = useState<'door' | 'alarm' | 'panel'>('panel');
  const [rows, setRows] = useState<PanelActivityRow[] | null>(null);
  useEffect(() => {
    setRows(null);
    getPanelActivity(cat, cat === 'panel' ? panelId : undefined).then(setRows).catch(() => setRows([]));
  }, [cat, panelId]);
  return (
    <div className="card" data-testid="panel-activity">
      <h2>{t('panels.act.title')}</h2>
      <div className="vms-ec-row">
        {(['door', 'alarm', 'panel'] as const).map((c) => (
          <button key={c} className={`evt-chip${cat === c ? ' active' : ''}`} aria-pressed={cat === c} onClick={() => setCat(c)}>
            {t(`panels.act.cat_${c}`)}
          </button>
        ))}
      </div>
      {rows && rows.length === 0 && <p className="text-secondary">{t('vmsActivity.empty')}</p>}
      <ul className="dv-panel-activity">
        {(rows ?? []).map((r, i) => {
          const { key, params } = panelActivityKey(r);
          const lk = panelActivityLevelKey(params.level); // 等級用 confirm.action.* 翻譯（三端同規則）
          if (lk) params.level = t(lk);
          return (
            <li key={`${r.ts_us}-${i}`}>
              <span className="text-secondary">{formatTimestampUs(r.ts_us)}</span> {r.actor_is_me ? t('vmsActivity.me') : r.actor} ·{' '}
              {stripEmptyParens(t(key, { ...params, defaultValue: r.action }))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** V-11 我的面板 PIN（帳號頁） */
export function MyPanelPinCard() {
  const { t } = useTranslation();
  const [st, setSt] = useState<MyPanelPin | null>(null);
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    getMyPanelPin().then(setSt).catch(() => setSt({ set: false }));
  }, []);
  useEffect(load, [load]);
  const save = async () => {
    const err = pinFormatError(pin);
    if (err) {
      setMsg(t(err === 'format' ? 'panels.pin.format' : 'panels.pin.weak'));
      return;
    }
    try {
      await putMyPanelPin(pin);
      setPin('');
      setMsg(t('panels.pin.saved'));
      load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const clear = async () => {
    try {
      await deleteMyPanelPin();
      setMsg(t('panels.pin.cleared'));
      load();
    } catch (e) {
      setMsg(errText(t, e));
    }
  };
  const pauses = activeUntil(st?.owner_pauses);
  const locks = activeUntil(st?.locks);
  return (
    <div className="card" data-testid="my-panel-pin">
      <h2>{t('panels.pin.title')}</h2>
      <p className="text-secondary">{t('panels.pin.hint')}</p>
      <p>{st?.set ? t('panels.pin.isSet') : t('panels.pin.notSet')}</p>
      {pauses.map(([o, until]) => (
        <p key={o} className="error-text">{t('panels.pin.paused', { time: fmtS(until) })}</p>
      ))}
      {locks.map(([p, until]) => (
        <p key={p} className="error-text">{t('panels.pin.locked', { time: fmtS(until) })}</p>
      ))}
      <label className="dv-pairing-input">
        <span>{st?.set ? t('panels.pin.change') : t('panels.pin.set')}</span>
        <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={12} value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} data-testid="my-pin-input" />
      </label>
      <div className="dv-pairing-actions">
        <button className="btn-primary" onClick={() => void save()} disabled={pin.length < 6}>{t('panels.disp.save')}</button>
        {st?.set && <button className="btn-ghost" onClick={() => void clear()}>{t('panels.pin.clear')}</button>}
      </div>
      {msg && <p className="text-secondary" data-testid="my-pin-msg">{msg}</p>}
    </div>
  );
}

/**
 * V-11「可以幫面板確認的手機」（帳號頁；10 §2.4、§7.2）：列出 / 刪除，冷卻中標示，最近的登記 / 刪除紀錄。
 * 網頁不能登記（M4：網頁不做手機確認），要加手機請在那支手機的親疼 App 設定。
 */
export function MyConfirmKeysCard() {
  const { t } = useTranslation();
  const [list, setList] = useState<ConfirmKeyList | null>(null);
  const [failed, setFailed] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    listConfirmKeys()
      .then((l) => {
        setList(l);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);
  useEffect(load, [load]);
  // 從通知列［看我的手機］進來（/account#my-phones）→ 讀到清單後捲到這張
  useEffect(() => {
    if (list && typeof window !== 'undefined' && window.location.hash === '#my-phones') {
      document.getElementById('my-phones')?.scrollIntoView?.({ block: 'start' });
    }
  }, [list]);
  const remove = async (keyId: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await deleteConfirmKey(keyId);
      setAsking(null);
      setMsg(t('confirm.my_phones_deleted'));
      load();
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const platformName = (p: string) => t(p === 'ios' ? 'confirm.platform_ios' : p === 'android' ? 'confirm.platform_android' : 'confirm.platform_other');
  const nowS = Date.now() / 1000;
  return (
    <div className="card" id="my-phones" data-testid="my-confirm-keys">
      <h2>{t('confirm.my_phones')}</h2>
      <p className="text-secondary">{t('confirm.my_phones_hint')}</p>
      {failed && <p className="error-text">{t('panels.errGeneric')}</p>}
      {list && list.keys.length === 0 && <p className="text-secondary" data-testid="my-confirm-keys-empty">{t('confirm.my_phones_empty')}</p>}
      {(list?.keys ?? []).map((k) => (
        <div key={k.key_id} className="dv-member-row" data-testid={`confirm-key-${k.key_id}`}>
          <div>
            <strong>{k.device_name || platformName(k.platform)}</strong>{' '}
            <span className="text-secondary">{platformName(k.platform)} · {t('confirm.my_phones_added', { time: fmtS(k.created_at) })}</span>
            {k.last_used_at ? <div className="text-secondary">{t('confirm.my_phones_last_used', { time: fmtS(k.last_used_at) })}</div> : null}
            {confirmKeyCooling(k, nowS) && (
              <div className="warn-text" data-testid={`confirm-key-${k.key_id}-cooling`}>{t('confirm.my_phones_cooldown', { time: fmtS(k.cooldown_until) })}</div>
            )}
          </div>
          {asking === k.key_id ? (
            <div className="dv-pairing-actions">
              <span>{t('confirm.my_phones_delete_ask', { name: k.device_name || platformName(k.platform) })}</span>
              <button className="btn-danger" disabled={busy} onClick={() => void remove(k.key_id)} data-testid={`confirm-key-${k.key_id}-delete-yes`}>
                {t('confirm.btn.delete')}
              </button>
              <button className="btn-ghost" disabled={busy} onClick={() => setAsking(null)}>{t('common.cancel')}</button>
            </div>
          ) : (
            <button className="btn-ghost" onClick={() => setAsking(k.key_id)} data-testid={`confirm-key-${k.key_id}-delete`}>{t('confirm.btn.delete')}</button>
          )}
        </div>
      ))}
      {list && list.history.length > 0 && (
        <>
          <h3>{t('confirm.my_phones_history')}</h3>
          <ul className="dv-panel-activity" data-testid="my-confirm-keys-history">
            {list.history.slice(0, 10).map((h, i) => (
              <li key={`${h.at}-${i}`}>
                {t(confirmKeyHistoryKey(h), { time: fmtS(h.at), name: h.device_name || platformName(h.platform ?? '') })}
              </li>
            ))}
          </ul>
        </>
      )}
      {msg && <p className="text-secondary" role="status">{msg}</p>}
    </div>
  );
}

/** V-22 快速設定（V-01 取名後；按完成才寫入；跳過 → 待辦出現對應項目） */
export function PanelQuickSetup({ panelId, devices, onDone }: { panelId: string; devices: DeviceSummary[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState<QuickSetup>(() => quickSetupDefaults(devices));
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const nameOf = useMemo(() => new Map(devices.map((d) => [d.device_id, deviceName(d)])), [devices]);
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  // 攝影機位置只透過「要不要 PIN」開關改（John 2026-10-21）；原本的位置記著，按完成只寫有改的
  const original = useMemo(() => new Map(quickSetupDefaults(devices).cameras.map((c) => [c.device_id, c.location])), [devices]);
  const [asking, setAsking] = useState<string | null>(null);
  const setLoc = (id: string, loc: QuickSetup['cameras'][number]['location']) =>
    setQ((cur) => ({ ...cur, cameras: cur.cameras.map((c) => (c.device_id === id ? { ...c, location: loc } : c)) }));
  /** 勾插播：從沒標過的攝影機當場問一次「這台拍的是家裡面嗎？」 */
  const toggleInterrupt = (id: string) => {
    const on = !q.interrupt.includes(id);
    setQ({ ...q, interrupt: toggle(q.interrupt, id) });
    const c = q.cameras.find((x) => x.device_id === id);
    if (on && c && cameraUntagged(c.location)) setAsking(id);
  };
  // 跳過 = 這一頁不寫入（待辦清單出現 no_doorbell / no_interrupt_camera）；最後一頁跳過 = 不送夜間、其餘照寫
  const skip = () => {
    if (page === 0) setQ({ ...q, doorbells: [] });
    if (page === 1) setQ({ ...q, interrupt: [] });
    if (page < 2) setPage(page + 1);
    else void finish(false);
  };
  const finish = async (withNight = true) => {
    setBusy(true);
    setMsg(null);
    try {
      for (const c of q.cameras) if (c.location && c.location !== original.get(c.device_id)) await setCameraLocation(c.device_id, c.location);
      await applyQuickSetup(panelId, q, withNight);
      onDone();
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dv-pairing-summary" data-testid="panel-quick-setup">
      {page === 0 && (
        <>
          <h3>{t('panels.qs.doorbells')}</h3>
          {q.doorbells.length === 0 && quickSetupDefaults(devices).doorbells.length === 0 && <p className="text-secondary">{t('panels.qs.noDoorbell')}</p>}
          {quickSetupDefaults(devices).doorbells.map((id) => (
            <label key={id} className="dv-check">
              <input type="checkbox" checked={q.doorbells.includes(id)} onChange={() => setQ({ ...q, doorbells: toggle(q.doorbells, id) })} />
              {nameOf.get(id) ?? id}
            </label>
          ))}
        </>
      )}
      {page === 1 && (
        <>
          <h3>{t('panels.qs.cameras')}</h3>
          {q.cameras.map((c) => (
            <div key={c.device_id} className="dv-member-row">
              <label className="dv-check">
                <input type="checkbox" data-testid={`qs-int-${c.device_id}`} checked={q.interrupt.includes(c.device_id)} onChange={() => toggleInterrupt(c.device_id)} />
                {nameOf.get(c.device_id) ?? c.device_id}
              </label>
              {/* 只有勾了要插播的才顯示開關（沒勾的不用設） */}
              {q.interrupt.includes(c.device_id) && <CameraPinToggle deviceId={c.device_id} location={c.location} onChange={(l) => setLoc(c.device_id, l)} />}
            </div>
          ))}
          {asking && (
            <CameraPinAsk name={nameOf.get(asking) ?? asking}
              onAnswer={(l) => { setLoc(asking, l); setAsking(null); }} onDismiss={() => setAsking(null)} />
          )}
        </>
      )}
      {page === 2 && (
        <>
          <h3>{t('panels.qs.night')}</h3>
          <label className="dv-check">
            <input type="checkbox" checked={q.night.on} onChange={() => setQ({ ...q, night: { ...q.night, on: !q.night.on } })} />
            {t('panels.qs.nightOn', { from: q.night.from, to: q.night.to })}
          </label>
        </>
      )}
      <div className="dv-pairing-actions">
        {page < 2 ? (
          <button className="btn-primary" onClick={() => setPage(page + 1)}>{t('panels.next')}</button>
        ) : (
          <button className="btn-primary" onClick={() => void finish(true)} disabled={busy} data-testid="qs-finish">{t('panels.qs.finish')}</button>
        )}
        <button className="btn-ghost" onClick={skip} data-testid="qs-skip">{t('panels.qs.skip')}</button>
      </div>
      {msg && <p className="error-text">{msg}</p>}
    </section>
  );
}
